import { useEffect, useState } from "react";

// Cookie consent (GDPR / ePrivacy). Google Analytics and Google ad cookies stay
// off until the visitor chooses (the defaults are set in public/index.html
// before any Google script runs). The choice is kept in this browser only.
const KEY = "partycade.consent";
const VERSION = 1;
const listeners = new Set();

export function getConsent() {
  try {
    const c = JSON.parse(localStorage.getItem(KEY));
    return c && c.v === VERSION ? c : null;
  } catch {
    return null;
  }
}

const adsQueue = () => (window.adsbygoogle = window.adsbygoogle || []);

// Tells Google's tags (Analytics and AdSense) what the visitor allowed.
function apply(c) {
  const on = (yes) => (yes ? "granted" : "denied");
  window.gtag?.("consent", "update", {
    analytics_storage: on(c.analytics),
    ad_storage: on(c.ads),
    ad_user_data: on(c.ads),
    ad_personalization: on(c.ads),
  });
  // Without ad consent AdSense only shows non-personalised ads.
  adsQueue().requestNonPersonalizedAds = c.ads ? 0 : 1;
  adsQueue().pauseAdRequests = 0;
}

export function saveConsent({ analytics, ads }) {
  const c = { v: VERSION, analytics: !!analytics, ads: !!ads, at: Date.now() };
  try {
    localStorage.setItem(KEY, JSON.stringify(c));
  } catch {}
  apply(c);
  listeners.forEach((fn) => fn(c));
}

// A certified consent platform (e.g. Google's own EU consent message, turned
// on in AdSense) handles consent itself; then our banner stays out of the way.
export const hasConsentPlatform = () => typeof window.__tcfapi === "function";

// With a consent platform in charge: may we store and read things on the
// visitor's device (TCF purpose 1)? Where the EU rules don't apply (the
// platform says gdprApplies: false) the answer is yes. Waits until the
// visitor has answered the platform's message.
export function platformAllowsDeviceAccess() {
  return new Promise((resolve) => {
    if (!hasConsentPlatform()) return resolve(false);
    window.__tcfapi("addEventListener", 2, (data, ok) => {
      if (!ok || !data || (data.eventStatus !== "tcloaded" && data.eventStatus !== "useractioncomplete")) return;
      window.__tcfapi("removeEventListener", 2, () => {}, data.listenerId);
      resolve(data.gdprApplies === false || !!data.purpose?.consents?.[1]);
    });
  });
}

// Runs once at startup: re-applies a saved choice, or holds ad requests until
// the visitor picks.
export function initConsent() {
  const c = getConsent();
  if (c) return apply(c);
  adsQueue().pauseAdRequests = 1;
  // Give a consent platform a moment to load before deciding who asks.
  setTimeout(() => {
    if (!hasConsentPlatform() || getConsent()) return;
    adsQueue().pauseAdRequests = 0;
    listeners.forEach((fn) => fn({ platform: true }));
  }, 2500);
}

// "Cookie settings" in the footer reopens the banner.
const OPEN_EVENT = "partycade:consent";
// How long Google's consent message gets to appear before we show ours.
export const GOOGLE_MESSAGE_WAIT_MS = 2000;
const googleMessageShown = () =>
  [...document.querySelectorAll(".fc-consent-root, .fc-dialog-container")].some((el) => el.getClientRects().length > 0);

export function openConsent() {
  const openOurs = () => window.dispatchEvent(new Event(OPEN_EVENT));
  // Google's consent message (if it's the one in charge) has its own settings
  // screen. It doesn't always appear (an ad blocker can stop it loading), so
  // if it hasn't shown up after a moment, our own banner opens instead.
  if (hasConsentPlatform() && window.googlefc?.callbackQueue) {
    try {
      window.googlefc.callbackQueue.push(() => window.googlefc.showRevocationMessage());
    } catch {}
    setTimeout(() => {
      if (!googleMessageShown()) openOurs();
    }, GOOGLE_MESSAGE_WAIT_MS);
    return;
  }
  openOurs();
}

export function useConsent() {
  const [consent, setConsent] = useState(getConsent);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onChange = (c) => {
      setConsent(c);
      setOpen(false);
    };
    const onOpen = () => setOpen(true);
    listeners.add(onChange);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => {
      listeners.delete(onChange);
      window.removeEventListener(OPEN_EVENT, onOpen);
    };
  }, []);
  return { consent, open, setOpen };
}
